import NamaCommon, {NamaAxiosRequestConfig} from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import {namaWebSocket} from "@/utils/broadcast/NamaWebSocketManager.ts";
import {useNotificationsStore} from "@/stores/ToastDataStore.ts";
import {useLoginInfoStore} from "@/stores/loginInfo.ts";
import {namaBroadcast} from "@/utils/broadcast/NamaBroadcastChannel.ts";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import ChatUtils from "@/utils/common/ChatUtils.ts";
import sound1 from "@/assets/sounds/sound1.mp3";
import sound2 from "@/assets/sounds/sound2.mp3";
import sound3 from "@/assets/sounds/sound3.mp3";
import sound4 from "@/assets/sounds/sound4.mp3";
import sound5 from "@/assets/sounds/sound5.mp3";
import {QuestionFieldsWithValues, TaskProgressPayload, WsChatUnreadPayload} from "@/utils/presenters/interfaces.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import SoundEffects from "@/utils/constants/SoundEffects.ts";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;

export default class NotificationUtils {
  static async updateTasks(payload: TaskProgressPayload | undefined) {
    if (!payload)
      payload = await NamaCommon.namaFetch(NamaURLS.GET_ALL_PENDING_GUI_ACTION_RESULTS, {noLoadingIndicator: true});
    if (payload && payload.results) {
      const tasks = (payload.results) ?? [];
      const count = payload.resultsCount ?? 0;
      if (count < (useLoginInfoStore()).tasksCount)
        this.runTaskSound();
      (useLoginInfoStore()).updateTasks(tasks, count);
      namaBroadcast.broadcastTasks(tasks, count);
    }
  }

  static async updateNotifications(playSound?: boolean) {
    let listTopNotificationsAndCount = await NamaCommon.namaFetch(NamaURLS.LIST__TOP_NOTIFICATIONS_AND_COUNT, this.NotificationsAndApprovalsRequestParams());
    const notifications = (listTopNotificationsAndCount?.notifications?.list) ?? [];
    const count = listTopNotificationsAndCount?.count ?? 0;
    const oldCount = (useLoginInfoStore()).notificationsCount;
    (useLoginInfoStore()).updateNotifications(notifications, count);
    namaBroadcast.broadcastNotifications(notifications, count);
    if (count > oldCount) {
      useNotificationsStore().pushNotifications((useLoginInfoStore()).notifications ?? []);
      if (playSound)
        this.runNotificationSound();
    }
  }

  static async updateApprovals(playSound?: boolean) {
    let listTopApprovalsAndCount = await NamaCommon.namaFetch(NamaURLS.LIST__TOP_APPROVALS_AND_COUNT, this.NotificationsAndApprovalsRequestParams());
    const approvals = (listTopApprovalsAndCount?.approvals?.list) ?? [];
    const count = listTopApprovalsAndCount?.count ?? 0;
    if (playSound && count > (useLoginInfoStore()).approvalsCount)
      this.runApprovalSound();
    (useLoginInfoStore()).updateApprovals(approvals, count);
    namaBroadcast.broadcastApprovals(approvals, count);
  }

  static async updateChatMessages(payload?: WsChatUnreadPayload) {
    if (!payload || ObjectChecker.isNullOrUndefined(payload.count)) {
      payload = {}
      payload.count = await ChatUtils.getUnreadCount(LoginUtils.currentUserInfo()?.id);
    }
    if (payload.refresh && payload.count > (useLoginInfoStore()).chatMessagesCounts)
      this.runChatSound();
    (useLoginInfoStore()).updateChatMessages(payload.count);
  }

  static async updateApprovalsAndNotificationsIfNeeded(playSound ?: boolean) {
    let data = await NamaCommon.namaFetch(NamaURLS.UPDATE_APPROVALS_AND_NOTIFICATIONS, this.NotificationsAndApprovalsRequestParams());
    if (data && data.counters) {
      if (data.counters.approvalsCount !== (useLoginInfoStore()).approvalsCount)
        await this.updateApprovals(playSound);
      if (data.counters.notificationsCount !== (useLoginInfoStore()).notificationsCount) {
        if (data.counters.notificationsCount > (useLoginInfoStore()).notificationsCount) {
          await this.updateNotifications(playSound);
          useNotificationsStore().pushNotifications((useLoginInfoStore()).notifications ?? []);
        } else {
          await this.updateNotifications();
        }
      }
    }
  }

  static async markAsReadTop5Notifications() {
    await NamaCommon.namaFetch(NamaURLS.MarkAsReadTop5Notifications, this.NotificationsAndApprovalsRequestParams())
    namaWebSocket.refreshNotifications();
  }

  private static NotificationsAndApprovalsRequestParams() {
    const settings: NamaAxiosRequestConfig = {
      params: {
        employeeId: LoginUtils.loginInfo?.userInfo?.employee?.id,
        userId: LoginUtils.loginInfo?.userInfo?.id
      },
      noLoadingIndicator: true,
    };
    return settings;
  }

  private static runApprovalSound() {
    const soundStr = useNamaFlagsStore().approvalsSound;
    const audio = this.createAudio(soundStr);
    if (audio)
      audio.play().catch(err => console.error('Failed to play approval sound:', err));
  }

  private static runNotificationSound() {
    const soundStr = useNamaFlagsStore().notificationsSound;
    const audio = this.createAudio(soundStr);
    if (audio)
      audio.play().catch(err => console.error('Failed to play notification sound:', err));
  }

  private static runChatSound() {
    const soundStr = useNamaFlagsStore().messagesSound;
    const audio = this.createAudio(soundStr);
    if (audio)
      audio.play().catch(err => console.error('Failed to play message sound:', err));
  }

  static runTaskSound() {
    const soundStr = useNamaFlagsStore().taskEndedSound;
    const audio = this.createAudio(soundStr);
    if (audio)
      audio.play().catch(err => console.error('Failed to play task sound:', err));
  }

  static createAudio(soundStr: string): HTMLAudioElement | undefined {
    if (soundStr === SoundEffects.None)
      return undefined;
    const soundMap = {
      Sound1: sound1,
      Sound2: sound2,
      Sound3: sound3,
      Sound4: sound4,
      Sound5: sound5
    };
    const sound = soundMap[soundStr as keyof typeof soundMap] ?? sound1;
    const audio = new Audio(sound);
    audio.volume = 0.5;
    return audio;
  }

  static getNotificationSound() {
    return this.getSound("notificationsSound")
  }

  static getApprovalSound() {
    return this.getSound("approvalsSound")
  }

  static getChatSound() {
    return this.getSound("messagesSound")
  }

  static getTaskSound() {
    return this.getSound("taskEndedSound")
  }

  static getSound(id: string) {
    let u = LoginUtils.currentUserInfo();
    if (u?.settings && u.settings[id])
      return u!.settings![id];
    if (u?.fullProfile && u.fullProfile[id])
      return u.fullProfile[id]
    let cgs = ClientCache.getCustomGUISettings();
    if (cgs && cgs[id])
      return cgs[id];
    return SoundEffects.Sound1;
  }

  static syncSoundsAfterLogin() {
    const store = useNamaFlagsStore();
    const soundKeys = ['notifications-sound', 'approvals-sound', 'messages-sound', 'task-ended-sound'] as const;
    const resolvedSounds = [this.getNotificationSound(), this.getApprovalSound(), this.getChatSound(), this.getTaskSound()];
    const storeUpdaters = [
      (v: string) => store.updateNotificationsSound(v),
      (v: string) => store.updateApprovalsSound(v),
      (v: string) => store.updateMessagesSound(v),
      (v: string) => store.updateTaskEndedSound(v),
    ];
    for (let i = 0; i < soundKeys.length; i++) {
      if (!localStorage.getItem(soundKeys[i]))
        storeUpdaters[i](resolvedSounds[i]);
    }
  }

  static createSoundQuestionFields(): QuestionFieldsWithValues {
    const store = useNamaFlagsStore();
    let soundQuestionFields: QuestionField[] = [];
    const allowedValues = [SoundEffects.None, SoundEffects.Sound1, SoundEffects.Sound2, SoundEffects.Sound3, SoundEffects.Sound4, SoundEffects.Sound5];
    soundQuestionFields.push(NamaCommon.createQuestionFieldWithAllowedValues("notificationsSound", 'SoundEffect', allowedValues));
    soundQuestionFields.push(NamaCommon.createQuestionFieldWithAllowedValues("approvalsSound", 'SoundEffect', allowedValues));
    soundQuestionFields.push(NamaCommon.createQuestionFieldWithAllowedValues("messagesSound", 'SoundEffect', allowedValues));
    soundQuestionFields.push(NamaCommon.createQuestionFieldWithAllowedValues("taskEndedSound", 'SoundEffect', allowedValues));
    let soundValues: DTOFilledQuestionField[] = [
      {
        id: "notificationsSound",
        objValue: store.notificationsSound,
        value: store.notificationsSound,
      },
      {
        id: "approvalsSound",
        objValue: store.approvalsSound,
        value: store.approvalsSound,
      },
      {
        id: "messagesSound",
        objValue: store.messagesSound,
        value: store.messagesSound,
      },
      {
        id: "taskEndedSound",
        objValue: store.taskEndedSound,
        value: store.taskEndedSound,
      },
    ];
    return {questionFields: soundQuestionFields, defaultValues: soundValues};
  }
}
